import React, { useState, useCallback, useEffect } from 'react';
import {
  StateMachineProvider,
  createStore,
  GlobalState,
  useStateMachine,
} from 'little-state-machine';
import { Routes, Route } from 'react-router';
import { useLocation } from 'react-router-dom';
import { EmptyState } from '../../../../components/EmptyState';
import { SetupJobPageLayout } from './SetupJobPageLayout';
import { SetupJobPostPage } from './SetupJobPostPage';
import { SetupJobApplicationFormPage } from './SetupJobApplicationFormPage';
import { SetupJobWorkflowPage } from './SetupJobWorkflowPage';
import { SetupJobDetailsPage } from './SetupJobDetailsPage';
import { getPathnameLastSegment } from '../../../../utils/url';
import { ItemPropTypes } from '../../../../components/HorizontalStepper';
import { CreateJobDto } from '../../../../services/applicant_tracking/JobService/CreateJobDto';
import { keyedUpdateAction } from '../../../../utils/reactHookForms';
import { JobDetails } from '../../../../services/applicant_tracking/JobService/EditJobDto';
import { JobPostService } from '../../../../services/applicant_tracking/JobPostService';
import { JobService } from '../../../../services/applicant_tracking/JobService';
import { Job } from '../../../../entities/applicant_tracking/Job';
import { JobPost } from '../../../../entities/applicant_tracking/JobPost';
import { SetupJobOrganicPostsPage } from './SetupJobOrganicPostsPage';

interface Proptypes {
  organicPostsEnabled: boolean;
  jobDetails?: JobDetails;
  jobFormDTO?: CreateJobDto;
}

export interface JobAndJobPost {
  job: Job;
  jobPost: JobPost;
}

export const initCreateJobDto: CreateJobDto = {
  jobId: null,
  jobPostId: null,
  name: null,
  departmentId: null,
  locationId: null,
  locationType: null,
  employmentType: null,
  jobOpenings: null,
  hiringTeamRecruiterId: null,
  hiringTeamMembers: [],
  editorState: null,
  editorStateHtml: null,
  applicationFormTemplateId: null,
  applicationFormTemplateConsent: false,
  jobWorkflowId: null,
  triggers: [],
};

function getStepperItems(organicPostsEnabled: boolean): ItemPropTypes[] {
  const stepperItems: ItemPropTypes[] = [
    {
      title: 'Job Details',
      subtitle: 'Add job main information',
      slug: 'job_details',
    },
    { title: 'Job Post', subtitle: 'Define job post', slug: 'job_post' },
    {
      title: 'Application Form',
      subtitle: 'Create application form',
      slug: 'application_form',
    },
    {
      title: 'Job Workflow',
      subtitle: 'Define process, stages and actions',
      slug: 'job_workflow',
    },
  ];

  if (!organicPostsEnabled) {
    return stepperItems;
  }

  return stepperItems.concat({
    title: 'Organic Posts',
    subtitle: 'Increase your reach',
    slug: 'organic_posts',
  });
}

function getRouteStep(routeName: string, stepperItems: ItemPropTypes[]) {
  return stepperItems.findIndex((item) => item.slug == routeName) || 0;
}

createStore(
  {
    setupJob: initCreateJobDto,
  },
  {
    storageType: localStorage,
  },
);

function Body(props: Proptypes) {
  const [jobDTO] = useState<CreateJobDto>(props.jobFormDTO || initCreateJobDto);
  const isEditFlow: boolean = props.jobFormDTO != null;
  const stepperItems = getStepperItems(props.organicPostsEnabled);

  const location = useLocation();
  const [setupStep, setSetupStep] = useState<number>(0);
  const boundedSetSetupStep = useCallback(
    (newStep: number, _jobDTO: CreateJobDto) => {
      if (newStep >= 0 && newStep < stepperItems.length) {
        setSetupStep(newStep);
      }
    },
    [setupStep],
  );
  const updateLocalStorage: (state: GlobalState, payload: any) => GlobalState =
    keyedUpdateAction('setupJob');

  const { actions, state } = useStateMachine({ updateLocalStorage });

  useEffect(() => {
    const routeName = getPathnameLastSegment(location.pathname);

    setSetupStep(getRouteStep(routeName, stepperItems));
  }, [location]);

  useEffect(() => {
    actions.updateLocalStorage(jobDTO);
  }, [jobDTO]);

  const handleSubmit = async (newState: CreateJobDto) => {
    const job = await JobService.upsert({
      id: newState.jobId,
      name: newState.name,
      departmentId: newState.departmentId,
      hiringTeamRecruiterId: newState.hiringTeamRecruiterId,
      hiringTeamMembers: newState.hiringTeamMembers,
      locationType: newState.locationType,
      employmentType: newState.employmentType,
      jobOpenings: newState.jobOpenings,
      planTemplateId: newState.jobWorkflowId,
      triggers: newState.triggers,
      isEditing: isEditFlow,
    });

    const jobPost = newState.editorState
      ? await JobPostService.upsert({
          name: newState.name,
          id: newState.jobPostId?.toString(),
          jobId: newState.jobId,
          locationId: newState.locationId,
          applicationFormTemplateId: newState.applicationFormTemplateId,
          editorState: newState.editorState,
          editorStateHtml: newState.editorStateHtml,
        })
      : null;

    return { job, jobPost };
  };

  return (
    <Routes>
      <Route
        element={
          <SetupJobPageLayout
            stepperItems={stepperItems}
            setupStep={setupStep}
            setSetupStep={boundedSetSetupStep}
            isEditFlow={isEditFlow}
            {...props}
          />
        }
      >
        <Route
          path={'job_details'}
          element={
            <SetupJobDetailsPage
              setupStep={setupStep}
              setSetupStep={boundedSetSetupStep}
              updateLocalStorage={updateLocalStorage}
              initJobDto={state.setupJob}
              isEditFlow={isEditFlow}
              jobDetails={props.jobDetails}
              onSubmit={handleSubmit}
            />
          }
        />
        <Route
          path={'job_post'}
          element={
            <SetupJobPostPage
              setupStep={setupStep}
              setSetupStep={boundedSetSetupStep}
              updateLocalStorage={updateLocalStorage}
              initJobDto={state.setupJob}
              isEditFlow={isEditFlow}
              jobDetails={props.jobDetails}
              onSubmit={handleSubmit}
            />
          }
        />
        <Route
          path={'application_form'}
          element={
            <SetupJobApplicationFormPage
              setupStep={setupStep}
              setSetupStep={boundedSetSetupStep}
              updateLocalStorage={updateLocalStorage}
              initJobDto={state.setupJob}
              isEditFlow={isEditFlow}
              jobDetails={props.jobDetails}
              onSubmit={handleSubmit}
            />
          }
        />
        <Route
          path={'job_workflow'}
          element={
            <SetupJobWorkflowPage
              setupStep={setupStep}
              setSetupStep={boundedSetSetupStep}
              updateLocalStorage={updateLocalStorage}
              initJobDto={state.setupJob}
              isEditFlow={isEditFlow}
              jobDetails={props.jobDetails}
              organicPostsEnabled={props.organicPostsEnabled}
              onSubmit={handleSubmit}
            />
          }
        />
        {props.organicPostsEnabled && (
          <Route
            path={'organic_posts'}
            element={
              <SetupJobOrganicPostsPage
                setupStep={setupStep}
                setSetupStep={boundedSetSetupStep}
                updateLocalStorage={updateLocalStorage}
                initJobDto={state.setupJob}
                isEditFlow={isEditFlow}
                jobDetails={props.jobDetails}
                onSubmit={handleSubmit}
              />
            }
          />
        )}
        <Route
          path={'*'}
          element={
            <EmptyState
              title={'Page not found!'}
              text={
                'For more information contact our support team at support@screenloop.com'
              }
              cardless={true}
            />
          }
        />
      </Route>
    </Routes>
  );
}

export function SetupJobPageRoutes(props: Proptypes) {
  return (
    <StateMachineProvider>
      <Body {...props} />
    </StateMachineProvider>
  );
}
